/**
 * SRN Shares hooks — directApiClient-backed search
 */

'use client';

import { useQuery } from '@tanstack/react-query';
import { directApiClient } from '@/lib/api/direct-client';
import { SrnShare } from './types';
import { PaginatedResponse } from '@/lib/types/api';

interface UseSrnSharesParams {
  searchQuery: string;
  page?: number;
  limit?: number;
}

interface UseSrnSharesReturn {
  data: PaginatedResponse<SrnShare> | undefined;
  isLoading: boolean;
  error: Error | null;
}

async function fetchSrnShares(
  searchQuery: string,
  page: number,
  limit: number
): Promise<PaginatedResponse<SrnShare>> {
  return directApiClient.get<PaginatedResponse<SrnShare>>('/srn-shares', {
    params: { q: searchQuery, page, limit }
  });
}

/**
 * Below the 2-character search minimum the picker shows a DEFAULT SHORTLIST rather
 * than nothing (VALE-1031): the shares most often selected across matters, which the
 * backend returns for `/srn-shares` with no `q`.
 *
 * Collapsing 0 and 1 characters onto the same empty query is deliberate. It gives
 * both the same react-query key, so typing the first character — and deleting it
 * again — reuses the cached shortlist instead of blanking the list and refetching.
 */
export function useSrnShares({
  searchQuery,
  page = 1,
  limit = 10
}: UseSrnSharesParams): UseSrnSharesReturn {
  const q = searchQuery.length >= 2 ? searchQuery : '';

  const query = useQuery<PaginatedResponse<SrnShare>, Error>({
    queryKey: ['srn-shares', q, page, limit],
    queryFn: () => fetchSrnShares(q, page, limit),
    staleTime: 30 * 1000
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error ?? null
  };
}

export type { SrnShare };
